import { useState, useEffect, useRef } from 'react'
import { useQuoteContext } from './useQuoteContext'
import useFeaturePermissions from './useFeaturePermissions'

// selectedProductCosts maps payroll to the aggregated total, whose
// volumeDiscountedPEPMPercent includes bundle/promotional discounts — not
// volume-only. Payroll is monitored per-EIN above instead.
const EXCLUDED_SELECTED_PRODUCT_COST_KEYS = new Set([
   'ongoingMonthlyTotal',
   'firstYearTotalCost',
   'payroll',
])

/**
 * Custom hook to monitor all products for 50% volume discount threshold
 * Includes: Core, Performance, Time Tracking, Benefits Admin, Payroll (all EINs), and Dynamic Products
 * Only alerts when the user has the LimitVolumeDiscount permission
 * Only triggers on user-typed EE count changes, not on initial data load (prefilled values)
 * Returns modal state and the product that triggered the alert
 */
export const useVolumeDiscountAlert = () => {
   const state = useQuoteContext()
   const { hasPermission } = useFeaturePermissions()
   const [showBigDeal, setShowBigDeal] = useState(false)
   const [alertProduct, setAlertProduct] = useState<string>('')

   // Use ref to store previous discounts to avoid stale closure issues
   const prevDiscountsRef = useRef<Map<string, number>>(new Map())
   // Track initial load: skip alerting on the first effect run (prefilled/loaded EE counts)
   const isInitialLoadRef = useRef(true)

   useEffect(() => {
      // Only check for internal users
      if (state.user_view !== 'Internal') return

      // Only alert if the user has the LimitVolumeDiscount permission (their discount is capped)
      if (!hasPermission('LimitVolumeDiscount', state.user_role)) return

      const products: Array<{ name: string; current: number; key: string }> = []

      // Core Products (HRIS - Pro/Elite)
      products.push({
         name: 'Core Product',
         current:
            state.productCoreMonthlyCosts?.volumeDiscountedPEPMPercent || 0,
         key: 'core',
      })

      // Performance Management
      products.push({
         name: 'Performance Management',
         current:
            state.productPerformanceMonthlyCosts?.volumeDiscountedPEPMPercent ||
            0,
         key: 'performance',
      })

      // Time Tracking
      products.push({
         name: 'Time Tracking',
         current:
            state.productTimeTrackingMonthlyCosts
               ?.volumeDiscountedPEPMPercent || 0,
         key: 'timeTracking',
      })

      // Benefits Administration
      products.push({
         name: 'Benefits Administration',
         current:
            state.productBenefitsAdministrationMonthlyCosts
               ?.volumeDiscountedPEPMPercent || 0,
         key: 'benAdmin',
      })

      // Payroll Products (array of EINs)
      if (
         state.productPayrollMonthlyCosts &&
         Array.isArray(state.productPayrollMonthlyCosts)
      ) {
         state.productPayrollMonthlyCosts.forEach((payrollCost, index) => {
            products.push({
               name: `Payroll EIN ${index + 1}`,
               current: payrollCost?.volumeDiscountedPEPMPercent || 0,
               key: `payroll_${index}`,
            })
         })
      }

      // Dynamic Monthly Products
      if (state.selectedProductCosts) {
         Object.keys(state.selectedProductCosts).forEach((productCode) => {
            if (EXCLUDED_SELECTED_PRODUCT_COST_KEYS.has(productCode)) {
               return
            }

            const productCost = state.selectedProductCosts[productCode]
            const product = state.product_list?.[productCode]

            // Only include if it's a monthly product with volume discount
            if (
               product &&
               productCost?.volumeDiscountedPEPMPercent !== undefined
            ) {
               products.push({
                  name: product.ProductName || productCode,
                  current: productCost.volumeDiscountedPEPMPercent || 0,
                  key: `dynamic_${productCode}`,
               })
            }
         })
      }

      // On initial load, only seed prevDiscountsRef without checking thresholds.
      // This prevents the alert from firing when EE counts are prefilled from saved data.
      if (isInitialLoadRef.current) {
         isInitialLoadRef.current = false
      } else {
         // Check each product for crossing 50% threshold
         for (const product of products) {
            const previous = prevDiscountsRef.current.get(product.key) || 0

            if (product.current >= 50 && previous < 50) {
               setAlertProduct(product.name)
               setShowBigDeal(true)
               break // Only show one alert at a time
            }
         }
      }

      // Update previous discounts for next comparison
      const newPrevDiscounts = new Map<string, number>()
      products.forEach((product) => {
         newPrevDiscounts.set(product.key, product.current)
      })
      prevDiscountsRef.current = newPrevDiscounts
   }, [
      state.productCoreMonthlyCosts?.volumeDiscountedPEPMPercent,
      state.productPerformanceMonthlyCosts?.volumeDiscountedPEPMPercent,
      state.productTimeTrackingMonthlyCosts?.volumeDiscountedPEPMPercent,
      state.productBenefitsAdministrationMonthlyCosts
         ?.volumeDiscountedPEPMPercent,
      state.productPayrollMonthlyCosts,
      state.selectedProductCosts,
      state.user_view,
   ])

   const handleClose = () => setShowBigDeal(false)

   return {
      showBigDeal,
      alertProduct,
      handleClose,
   }
}
